
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { RoutesPath } from '../../../../../shared/constants/routes-path';
import { Router } from '@angular/router';
import { TablePagenationComponent } from '../../../../../shared/components/table-pagenation/table-pagenation.component';
import { SharedService } from '../../../../../shared/services/shared.service';

@Component({
  selector: 'app-membership-users',
  imports: [TranslateModule, ...SHARED_COMPONENTS, TablePagenationComponent],
  templateUrl: './membership-users.component.html',
  styleUrl: './membership-users.component.scss'
})
export class MembershipUsersComponent implements OnChanges {
  @Input() users: any[] = [];
  @Input() loader: boolean = false;
  @Input() isPrimaryUser: boolean = false;
  @Input() memberNumber: string = "";

  @Output() unlinkUser = new EventEmitter<any>();
  pageSize: number = 10;
  currentPage: number = 1;
  searchTerm: string = '';
  filteredUsers: any[] = [];

  readonly searchFields: string[] = [
    'DC Nationality',
    'Status',
    'User Type',
    'First Name',
    'DC Name Arabic',
    'DC Name English',
    'DC Admin Flag',
    'Cellular Phone #',
    'Work Phone #',
    'Emirates IdEmirates Id',
    'Id',
    'Login Name',
    'Last Name',
    'Email Address'
  ];
  // This method is used to initialize the class instance using router
  constructor(
    private router: Router,
    private sharedService:SharedService
  ) {
  }

   isSameUserAsLogin(user:any){
    let userLoginName = this.sharedService.getUserProfile()['Login Name'];
    if(userLoginName == user['Login Name']){
      return true;
    }
    return false;
  }
  // This method is used to handle search using event
  handleSearch(event: any) {
    this.searchTerm = (event?.target?.value ?? '').trim().toLowerCase();
    this.applyFilters();
  }

  // This method is used to view using row
  view(user: any) {
    if (user['Login Name']) {
      let url: any = [`${RoutesPath.USER_DETAIL}`, this.memberNumber, user['Login Name']];
      this.router.navigate(url);
    }
  }
  // This method is used to edit using id
  edit(id: any) {
    this.router.navigate([RoutesPath.SERVICE + "/" + RoutesPath.UPDATE_USER, 1])
  }
  // This method is used to add user
  addUser() {
    this.router.navigate([RoutesPath.SERVICE + "/" + RoutesPath.ADD_USER])
  }
  // This method is used to remove using user
  remove(user: any) {
    this.unlinkUser.emit(user);
  }
  // This method is used to re-align pagination when users change
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users']) {
      this.applyFilters();
    }
  }

  get totalPages(): number {
    const total = this.filteredUsers?.length ?? 0;
    return total > 0 ? Math.ceil(total / this.pageSize) : 0;
  }

  // This method is used to get users for the active page
  get paginatedUsers(): any[] {
    if (!Array.isArray(this.filteredUsers)) {
      return [];
    }
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredUsers.slice(start, start + this.pageSize);
  }

  // This method is used to move to a specific page
  goToPage(page: number): void {
    if (!this.totalPages) {
      return;
    }
    const target = Math.min(Math.max(page, 1), this.totalPages);
    if (target !== this.currentPage) {
      this.currentPage = target;
    }
  }

  // This method is used to move to the previous page
  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  // This method is used to move to the next page
  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  private syncCurrentPage(): void {
    if (this.totalPages === 0) {
      this.currentPage = 1;
      return;
    }
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  private applyFilters(): void {
    const sourceUsers = Array.isArray(this.users) ? this.users : [];
    if (!this.searchTerm) {
      this.filteredUsers = [...sourceUsers];
      this.currentPage = 1;
      this.syncCurrentPage();
      return;
    }

    this.filteredUsers = sourceUsers.filter((user) => this.matchesSearch(user, this.searchTerm));
    this.currentPage = 1;
    this.syncCurrentPage();
  }

  private matchesSearch(user: any, term: string): boolean {
    const values: string[] = [
      ...this.searchFields.map((field) => this.toSearchValue(user?.[field])),
      this.toSearchValue(user?.ContactXM?.['Account Id']),
      this.toSearchValue(user?.ContactXM?.['DC User Type'])
    ];

    return values.some((value) => value.includes(term));
  }

  private toSearchValue(value: any): string {
    return String(value ?? '').toLowerCase();
  }
}
